import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import fiCommon from '@/locales/fi/common.json';
import enCommon from '@/locales/en/common.json';

import { setApiLanguage } from './api';
import { useUIStore } from './store';

/*
 * i18next-konfiguraatio.
 *
 * Resurssit:
 *  - common-namespace nyt. Moduulikohtaisia namespaceja (project, campaign jne.)
 *    lisataan kun moduulit kasvavat.
 *  - FI ja EN.
 *
 * Kielen valinta:
 *  - Default fi (Luviamon ensimmainen markkina on Suomi).
 *  - LanguageDetector tarkistaa: 1) localStorage-avain "i18nextLng",
 *    2) navigator.language, 3) htmlTag. Tama on kaytossa toistaiseksi,
 *    mutta storen `locale` (luviamo-ui-prefs) on todellinen lahde:
 *    storenLocaleSync()-funktio synkronoi storen muutokset i18n:aan.
 *  - SettingsPage:n kielenvaihtonappi rakennetaan Sessio 10:ssa.
 *
 * Interpoloinnit Reactissa: oletuksena {{name}}-syntaksi, escape pois paalta
 * (React tekee oman XSS-suojansa).
 */

const resources = {
  fi: { common: fiCommon },
  en: { common: enCommon },
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'fi',
    supportedLngs: ['fi', 'en'],
    defaultNS: 'common',
    ns: ['common'],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
  });

/*
 * Synkronoi `useUIStore.locale` -> i18next.language.
 *
 * Storen muutokset levitetaan i18nextiin, jotta yksi kielenvaihto-actio
 * paivittaa molemmat (ja siten myos UI-komponentit jotka kayttavat t():ta
 * ja komponentit jotka lukevat suoraan storesta).
 *
 * Toiseen suuntaan synkronointia ei tarvita: i18n:n LanguageDetector kaytetaan
 * vain alkulataukseen, sen jalkeen kayttajan tekema kielenvaihto kulkee aina
 * storen kautta.
 */
useUIStore.subscribe((state, prev) => {
  if (state.locale !== prev.locale) {
    void i18n.changeLanguage(state.locale);
    setApiLanguage(state.locale);
    syncHtmlLang(state.locale);
  }
});

/*
 * html lang seuraa valittua kieltä (BUG-H1, 30.10.2026). index.html:n lang="fi" on vain alkuarvo:
 * englanninkieliselle käyttöliittymälle Chrome tarjosi suomen käännöstä, ja kääntäjän DOM-muutos
 * kaataa Reactin (ks. RouteErrorPage). Oikea lang on myös ruudunlukijoiden ääntämisen edellytys.
 */
function syncHtmlLang(locale: string): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

// Alkulataus: jos storen locale poikkeaa i18n:n paatoksesta, kaytetaan storea
const initialLocale = useUIStore.getState().locale;
if (i18n.language !== initialLocale) {
  void i18n.changeLanguage(initialLocale);
}
// Palvelimen tuottamat tekstit seuraavat samaa valintaa (Accept-Language, ks. api.ts setApiLanguage).
setApiLanguage(initialLocale);
syncHtmlLang(initialLocale);

export default i18n;
